import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, MapPin, Loader2, Menu, X, AlertTriangle, List, Map, LocateFixed, Navigation, ArrowLeft, CalendarCheck, PawPrint, Receipt, FileCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Logo, LogoMark } from '../components/Logo';
import { StarRating } from '../components/StarRating';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// Lazy load MapView for better performance
const MapView = lazy(() => import('../components/MapView'));

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  const [serviceTypes, setServiceTypes] = useState([]);
  const [providers, setProviders] = useState([]);
  const [mapProviders, setMapProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  
  // Filter states
  const [serviceType, setServiceType] = useState('all');
  const [providerCategory, setProviderCategory] = useState('all');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [miles, setMiles] = useState([50]);

  // Distance search (Session 23): user coords come from browser geolocation
  // or from geocoding their typed city/state via Nominatim. When set, the
  // search uses Haversine distance filtering and sorts nearest-first.
  const [userCoords, setUserCoords] = useState(null); // {latitude, longitude, label?}
  const [locating, setLocating] = useState(false);

  // Category options with friendly labels
  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'FARRIER', label: 'Farrier' },
    { value: 'RIDING_INSTRUCTOR', label: 'Riding Instructor' },
    { value: 'TRAINER', label: 'Trainer' },
    { value: 'VET', label: 'Veterinarian' },
    { value: 'HAULING', label: 'Horse Hauling' },
    { value: 'RANCH_SERVICE', label: 'Ranch Services' },
    { value: 'FENCE_REPAIR', label: 'Fence Repair' },
    { value: 'EQUIPMENT_WORK', label: 'Equipment Work' },
    { value: 'GUIDED_TRAIL_RIDES', label: 'Guided Trail Rides' },
    { value: 'RIDING_STABLE', label: 'Riding Stable' },
    { value: 'STABLE_SERVICES', label: 'Stable Services' },
  ];

  // Load service types on mount
  useEffect(() => {
    loadServiceTypes();
    searchProviders(); // Initial load
    loadMapProviders(); // Load map data
  }, []);

  const loadServiceTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/services/types`);
      setServiceTypes(response.data);
    } catch (error) {
      console.error('Error loading service types:', error);
    }
  };

  const loadMapProviders = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/providers/map`);
      setMapProviders(response.data);
    } catch (error) {
      console.error('Error loading map providers:', error);
    }
  };

  const searchProviders = async (overrideCoords = null) => {
    setLoading(true);
    try {
      const coords = overrideCoords ?? userCoords;
      const params = new URLSearchParams();
      if (serviceType && serviceType !== 'all') params.append('serviceType', serviceType);
      if (providerCategory && providerCategory !== 'all') params.append('category', providerCategory);
      if (coords) {
        params.append('latitude', coords.latitude);
        params.append('longitude', coords.longitude);
        params.append('radius_miles', miles[0]);
      } else {
        if (city) params.append('city', city);
        if (state) params.append('state', state);
        params.append('miles', miles[0]);
      }

      const response = await axios.get(`${API_URL}/api/providers/search?${params.toString()}`);
      setProviders(response.data);
    } catch (error) {
      console.error('Error searching providers:', error);
      setProviders([]);
    } finally {
      setLoading(false);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Your browser doesn't support location");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          label: 'My current location',
        };
        setUserCoords(c);
        setLocating(false);
        toast.success('Location set — showing providers nearest to you');
        searchProviders(c);
      },
      (err) => {
        setLocating(false);
        toast.error(
          err.code === 1
            ? 'Location permission denied — type your city/state instead'
            : 'Could not get your location'
        );
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    );
  };

  const geocodeTypedLocation = async () => {
    const q = [city, state].filter(Boolean).join(', ').trim();
    if (!q) {
      toast.error('Enter a city or state first');
      return;
    }
    setLocating(true);
    try {
      const res = await axios.get(`${API_URL}/api/geocode`, { params: { q } });
      if (res.data?.found) {
        const c = {
          latitude: res.data.latitude,
          longitude: res.data.longitude,
          label: res.data.displayName || q,
        };
        setUserCoords(c);
        toast.success(`Found: ${res.data.displayName || q}`);
        searchProviders(c);
      } else if (res.data?.rateLimited) {
        toast.error('Geocoding is busy — please try again in a few seconds');
      } else {
        toast.error("Couldn't geocode that — try a different spelling");
      }
    } catch {
      toast.error('Geocoding failed — try again');
    } finally {
      setLocating(false);
    }
  };

  const clearLocation = () => {
    setUserCoords(null);
    toast.info('Location filter cleared');
  };

  // Re-run search automatically when the user adjusts the radius
  // *while* a coord-based location is active. Debounced via setTimeout.
  useEffect(() => {
    if (!userCoords) return;
    const t = setTimeout(() => searchProviders(), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miles, userCoords]);

  const handleSearch = (e) => {
    e.preventDefault();
    searchProviders();
  };

  return (
    <div className="min-h-screen">
      {/* Navigation Header */}
      <nav className="bg-card border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Logo variant="compact" size="sm" />

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4">
              <Button
                variant="destructive"
                size="sm"
                onClick={() => navigate('/emergency-finder')}
                data-testid="emergency-button"
              >
                <AlertTriangle className="mr-2 h-4 w-4" />
                Emergency
              </Button>
              {isClient ? (
                <Button
                  onClick={() => navigate('/client/dashboard')}
                  data-testid="client-dashboard-link"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Client Dashboard
                </Button>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/login')}
                    data-testid="client-login-link"
                  >
                    Client Login
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/login')}
                    data-testid="provider-login-link"
                  >
                    Provider Login
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/client/signup')}
                    data-testid="client-signup-link"
                  >
                    Create Client Account
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/provider/signup')}
                    data-testid="provider-signup-link"
                  >
                    Become a Provider
                  </Button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t" data-testid="mobile-menu">
              <div className="flex flex-col gap-2">
                <Button
                  variant="destructive"
                  className="w-full justify-start"
                  onClick={() => {
                    navigate('/emergency-finder');
                    setMobileMenuOpen(false);
                  }}
                >
                  <AlertTriangle className="mr-2 h-4 w-4" />
                  Emergency Service
                </Button>
                {isClient ? (
                  <Button
                    className="w-full justify-start"
                    onClick={() => {
                      navigate('/client/dashboard');
                      setMobileMenuOpen(false);
                    }}
                    data-testid="mobile-client-dashboard-link"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Client Dashboard
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="ghost"
                      className="w-full justify-start"
                      onClick={() => {
                        navigate('/login');
                        setMobileMenuOpen(false);
                      }}
                      data-testid="mobile-client-login-link"
                    >
                      Client Login
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full justify-start"
                      onClick={() => {
                        navigate('/login');
                        setMobileMenuOpen(false);
                      }}
                      data-testid="mobile-provider-login-link"
                    >
                      Provider Login
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => {
                        navigate('/provider/signup');
                        setMobileMenuOpen(false);
                      }}
                    >
                      Become a Provider
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div 
        className="relative bg-cover bg-center py-20 md:py-32"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1690337640885-d080e30500c7?crop=entropy&cs=srgb&fm=jpg&q=85)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Hero Logo */}
          <div className="flex justify-center mb-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 inline-flex items-center gap-3">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
                <span className="font-bold text-primary text-2xl md:text-3xl">RW</span>
              </div>
              <div className="text-left">
                <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight" data-testid="hero-title">
                  RanchWorks
                </h1>
                <p className="text-sm md:text-base text-white/80 font-medium" data-testid="hero-tagline">
                  Book what your ranch needs
                </p>
              </div>
            </div>
          </div>
          <p className="text-base md:text-lg text-white/90 max-w-2xl mx-auto mb-8" data-testid="hero-subtitle">
            Find and book trusted ranch and equine service providers in your area.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-primary hover:bg-white/90 font-semibold px-8"
              onClick={() => document.getElementById('search-form').scrollIntoView({ behavior: 'smooth' })}
              data-testid="find-services-btn"
            >
              Find Services
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10 font-semibold px-8"
              onClick={() => navigate('/provider/signup')}
              data-testid="list-business-btn"
            >
              List Your Business
            </Button>
          </div>
        </div>
      </div>

      {/* Built for Rural Service Providers */}
      <div className="bg-muted/40 border-b" data-testid="provider-benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20 md:pt-16 md:pb-24">
          <h2 className="text-lg font-bold tracking-tight" data-testid="provider-benefits-heading">
            Built for Rural Service Providers
          </h2>
          <p className="text-sm md:text-base text-muted-foreground mt-2 max-w-2xl">
            RanchWorks helps rural service providers manage appointments, clients, animal records, and invoices in one place. Less paperwork. Fewer missed details. More time back.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            {[
              { icon: CalendarCheck, label: 'Appointments' },
              { icon: PawPrint, label: 'Client & Animal Records' },
              { icon: Receipt, label: 'Invoicing' },
              { icon: FileCheck, label: 'Less Paperwork' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-3 rounded-md border bg-card p-3" data-testid={`benefit-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <Icon className="h-5 w-5 text-primary shrink-0" />
                <span className="text-sm font-medium">{label}</span>
              </div>
            ))}
          </div>
          <Button
            className="mt-6 font-semibold px-8"
            onClick={() => navigate('/provider/signup')}
            data-testid="join-ranchworks-btn"
          >
            Join RanchWorks
          </Button>
        </div>
      </div>

      {/* Search Filters */}
      <div className="bg-card shadow-lg -mt-12 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <form onSubmit={handleSearch} id="search-form" className="space-y-6" data-testid="search-form">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Provider Category */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-foreground" htmlFor="provider-category">
                  Provider Type
                </label>
                <Select value={providerCategory} onValueChange={setProviderCategory}>
                  <SelectTrigger id="provider-category" data-testid="category-select" className="h-12">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Service Type */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-foreground" htmlFor="service-type">
                  Service Type
                </label>
                <Select value={serviceType} onValueChange={setServiceType}>
                  <SelectTrigger id="service-type" data-testid="service-type-select" className="h-12">
                    <SelectValue placeholder="All Services" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Services</SelectItem>
                    {serviceTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* City */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-foreground" htmlFor="city">
                  City
                </label>
                <Input
                  id="city"
                  data-testid="city-input"
                  type="text"
                  placeholder="Enter city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="h-12"
                />
              </div>

              {/* State */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-foreground" htmlFor="state">
                  State
                </label>
                <Input
                  id="state"
                  data-testid="state-input"
                  type="text"
                  placeholder="Enter state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="h-12"
                />
              </div>
            </div>

            {/* Miles Slider and Search Button Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-end">
              {/* Miles Slider */}
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <label className="text-sm font-semibold text-foreground">
                    Within {miles[0]} miles
                  </label>
                  {userCoords ? (
                    <span
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-primary/10 text-primary border border-primary/20 max-w-[260px]"
                      data-testid="active-location-pill"
                    >
                      <Navigation className="h-3 w-3 shrink-0" />
                      <span className="truncate">{userCoords.label}</span>
                      <button
                        type="button"
                        onClick={clearLocation}
                        className="ml-1 text-primary/70 hover:text-primary"
                        data-testid="clear-location-btn"
                        aria-label="Clear location"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ) : null}
                </div>
                <Slider
                  data-testid="miles-slider"
                  value={miles}
                  onValueChange={setMiles}
                  min={5}
                  max={250}
                  step={5}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>5 mi</span>
                  <span>250 mi</span>
                </div>
                {/* Location helpers */}
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={useMyLocation}
                    disabled={locating}
                    data-testid="use-my-location-btn"
                  >
                    {locating ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <LocateFixed className="h-3 w-3 mr-1" />
                    )}
                    Use my location
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={geocodeTypedLocation}
                    disabled={locating || (!city && !state)}
                    data-testid="geocode-typed-btn"
                  >
                    <MapPin className="h-3 w-3 mr-1" />
                    Use typed city/state
                  </Button>
                </div>
              </div>

              {/* Search Button - Desktop */}
              <div className="hidden lg:block">
                <Button 
                  type="submit" 
                  className="w-full h-12 font-semibold"
                  data-testid="search-button"
                >
                  <Search className="mr-2 h-5 w-5" />
                  Search
                </Button>
              </div>
            </div>

            {/* Search Button - Mobile */}
            <div className="lg:hidden">
              <Button 
                type="submit" 
                className="w-full h-12 font-semibold"
                data-testid="search-button-mobile"
              >
                <Search className="mr-2 h-5 w-5" />
                Search Providers
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Results Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 pb-32">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
          <div>
            <h2 className="text-3xl font-bold tracking-tight" data-testid="results-heading">
              Available Providers
            </h2>
            <p className="text-muted-foreground mt-2">
              {loading ? 'Searching...' : `${providers.length} provider${providers.length === 1 ? '' : 's'} found`}
            </p>
          </div>
          
          {/* View Toggle */}
          <div className="flex items-center gap-2 p-1 bg-muted rounded-lg" data-testid="view-toggle">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="gap-2"
              data-testid="list-view-button"
            >
              <List className="h-4 w-4" />
              <span className="hidden sm:inline">List</span>
            </Button>
            <Button
              variant={viewMode === 'map' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('map')}
              className="gap-2"
              data-testid="map-view-button"
            >
              <Map className="h-4 w-4" />
              <span className="hidden sm:inline">Map</span>
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20" data-testid="loading-indicator">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : viewMode === 'map' ? (
          /* Map View */
          <Suspense fallback={
            <div className="h-[500px] bg-muted rounded-lg flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          }>
            <div className="relative">
              <MapView providers={mapProviders} />
            </div>
          </Suspense>
        ) : providers.length === 0 ? (
          <div className="text-center py-20 px-4" data-testid="no-results">
            <div className="max-w-md mx-auto">
              <div className="bg-muted/50 rounded-full p-4 w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <Search className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold mb-2">No providers listed yet</h3>
              <p className="text-muted-foreground mb-6">
                Check back soon or join as a provider.
              </p>
              <Button 
                onClick={() => navigate('/provider/signup')}
                size="lg"
                data-testid="list-business-button"
              >
                List Your Business
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="provider-results">
            {providers.map((provider) => (
              <Card 
                key={provider.id} 
                className="hover:shadow-md transition-shadow duration-200"
                data-testid={`provider-card-${provider.slug}`}
              >
                <CardHeader>
                  <CardTitle className="text-xl">{provider.businessName}</CardTitle>
                  <CardDescription className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {provider.city}, {provider.state}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Rating Display */}
                  <div className="mb-3">
                    {provider.reviewCount > 0 ? (
                      <div className="flex items-center gap-2">
                        <StarRating rating={provider.averageRating} size="sm" />
                        <span className="text-sm font-medium">{provider.averageRating?.toFixed(1)}</span>
                        <span className="text-xs text-muted-foreground">
                          ({provider.reviewCount} {provider.reviewCount === 1 ? 'review' : 'reviews'})
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">No reviews yet</span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-3">
                    {provider.bio}
                  </p>
                  <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                    <span className="font-semibold">Service Radius:</span>
                    <span>{provider.serviceRadiusMiles} miles</span>
                    {typeof provider.distanceMiles === 'number' && (
                      <span
                        className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 font-semibold"
                        data-testid={`distance-pill-${provider.slug}`}
                      >
                        <Navigation className="h-3 w-3" />
                        {provider.distanceMiles < 1
                          ? '< 1 mi'
                          : `${Math.round(provider.distanceMiles)} mi away`}
                      </span>
                    )}
                  </div>
                </CardContent>
                <CardFooter>
                  <Button 
                    className="w-full font-semibold"
                    onClick={() => window.location.href = `/providers/${provider.slug}`}
                    data-testid={`book-button-${provider.slug}`}
                  >
                    Book Service
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-card border-t mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand Column */}
            <div className="md:col-span-2">
              <Logo variant="full" size="md" linkTo="/" />
              <p className="mt-4 text-sm text-muted-foreground max-w-md">
                RanchWorks is a marketplace and booking platform where ranch and equine service providers can create profiles and allow clients to book their services online.
              </p>
            </div>
            
            {/* Provider Examples Column */}
            <div>
              <h4 className="font-semibold mb-4">Find Providers</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>Farriers</li>
                <li>Riding Instructors</li>
                <li>Horse Trainers</li>
                <li>Horse Hauling</li>
                <li>Mobile Vets</li>
              </ul>
            </div>
            
            {/* Links Column */}
            <div>
              <h4 className="font-semibold mb-4">For Providers</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate('/provider/signup')}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    List Your Business
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => navigate('/login')}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Provider Login
                  </button>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Copyright */}
          <div className="border-t mt-8 pt-8 text-center text-sm text-muted-foreground">
            <p>&copy; {new Date().getFullYear()} RanchWorks. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}